import {isMediaString, MediaString} from '@wandb/weave/common/types/media';
import {Icon} from '@wandb/weave/components/Icon';
import {RemovableTag, RemoveAction} from '@wandb/weave/components/Tag';
import {clsx} from 'clsx';
import * as React from 'react';

import {
  Maybe,
  RunValueSuggestionsPaginatedDocument,
} from '../../generated/graphql';
import * as Run from '../../util/runs';
import {makeInfiniteDropdown} from './InfiniteDropdown';

const Container = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
  }
>(({children}, ref) => {
  return (
    <div ref={ref} className="relative max-w-[400px] flex-grow">
      {children}
    </div>
  );
});

const Display = ({
  isOpen,
  selectedItems,
  removeFilter,
  isMultiSelect = false,
  isValid = true,
  toggleMenu,
  ...props
}: {
  isOpen: boolean;
  selectedItems: string[];
  removeFilter: (item: string) => void;
  isMultiSelect: boolean;
  isValid?: boolean;
  toggleMenu: () => void;
}) => {
  const classes = clsx(
    'max-h-[250px] overflow-y-auto',
    'min-h-[44px] min-w-[220px]',
    'w-full py-[8px] px-[13px] sticky flex flex-wrap items-center gap-4 justify-items-start',
    isValid ? 'bg-[rgba(46,120,199,0.08)]' : 'bg-red-300/[.32]'
  );

  if (selectedItems.length === 0) {
    return (
      <div
        className={classes}
        onClick={toggleMenu}
        tabIndex={0}
        data-test="filter-value"
        {...props}>
        <div className="self-center">Select a value...</div>
      </div>
    );
  }

  return (
    <div
      className={classes}
      onClick={toggleMenu}
      tabIndex={0}
      data-test="filter-value"
      {...props}>
      {isMultiSelect ? (
        selectedItems.map(selItem => {
          return (
            <RemovableTag
              color="blue"
              key={`selected-item-${selItem}`}
              label={selItem ?? ''}
              removeAction={
                <RemoveAction
                  onClick={(e: any) => {
                    e.stopPropagation();
                    removeFilter(selItem);
                  }}
                />
              }
              Wrapper={null}></RemovableTag>
          );
        })
      ) : (
        <div className="block w-[400px] items-center truncate p-2">
          {selectedItems[0]}
        </div>
      )}
    </div>
  );
};
const ListWrapper = React.forwardRef<
  HTMLUListElement,
  {
    children: React.ReactNode;
    isOpen: boolean;
    toggleOpen: (isOpen?: boolean) => void;
  }
>((props, ref) => {
  const {children, isOpen, toggleOpen, ...otherProps} = props;
  const classes = clsx(
    'absolute mt-8 w-full list-none rounder p-0 z-[10]',
    'border-[1px] border-solid border-[rgba(34,36,38,.15)] overflow-hidden',
    'bg-[#fff] max-h-[250px]',
    'shadow-[0_2px_4px_0-rgba(34,36,38,.12),0_2px_10px_0-rgba(34,36,38,.15)]',
    {hidden: !isOpen}
  );

  return (
    <ul
      className={classes}
      ref={ref}
      {...otherProps}
      data-test="filter-simple-value-dropdown-wrapper">
      {props.children}
    </ul>
  );
});

const ListItem = React.forwardRef<
  HTMLLIElement,
  {
    isHighlighted: boolean;
    isSelected: boolean;
    value: string;
  }
>((props, ref) => {
  const {isHighlighted, ...otherProps} = props;
  const classes = clsx(
    'hover:bg-[rgba(0,0,0,.05)] hover:text-[rgba(0,0,0,.95)]',
    'truncate leading-18 m-0 px-12 py-[13px] cursor-pointer item',
    {'bg-[rgba(0,0,0,.05)] text-[rgba(0,0,0,.95)]': isHighlighted}
  );
  return <li ref={ref} className={classes} {...otherProps} />;
});

const ItemLabel = ({
  isSelected,
  label,
}: {
  isSelected: boolean;
  label: string;
}) => {
  return (
    <div className="flex w-full justify-between">
      <span className="truncate">{label}</span>
      {isSelected && <Icon name="checkmark" />}
    </div>
  );
};

const InputElement = React.forwardRef((props: any, ref) => {
  const {className, ...otherProps} = props;
  delete otherProps.toggleSelection;

  return (
    <input
      className={`m-0 w-full cursor-text overflow-visible border-0 bg-white px-12 py-5 text-base leading-none shadow-none focus-visible:border-0`}
      ref={ref}
      placeholder={`Search values`}
      {...otherProps}
      // this has to come after the spread to avoid accidental overwriting
      onClick={e => {
        e.stopPropagation();
      }}
    />
  );
});

export const FilterSimpleValueInfiniteDropdown = makeInfiniteDropdown(
  {
    Container,
    Display,
    Input: InputElement,
    ListItem,
    ItemLabel,
    ListWrapper,
  },
  {
    extractItems: (data: any): string[] => {
      return (
        data?.project?.runFieldValues?.edges
          ?.filter((edge: {node: {value: Maybe<string>}}) => {
            if (edge.node.value == null) {
              return false;
            }
            try {
              const parsedValue = JSON.parse(edge.node.value);
              const valueType = parsedValue?._type;
              // Since media types aren't simple values, we just want to enable filtering by null/non-null for now. So filter out
              // media values.
              return !isMediaString(valueType as MediaString);
            } catch (e) {
              // in case of JSON parse error, we don't want to show the value
              return false;
            }
          })
          ?.map((edge: {node: {value: Maybe<string>}}) => {
            return Run.displayValue(edge.node.value, 'null');
          }) ?? []
      );
    },
    extractPageInfo: (data: any) => {
      return data?.project?.runFieldValues?.pageInfo ?? {};
    },
    query: RunValueSuggestionsPaginatedDocument,
  }
);
